import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import { TrendingUp, ArrowLeft, Play, Eye, CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const WIP_METHODS = [
  { value: 'POC', label: 'Percentage of Completion (POC)' },
  { value: 'COST_OF_SALES', label: 'Cost of Sales' },
  { value: 'COMPLETED_CONTRACT', label: 'Completed Contract' },
  { value: 'REVENUE_BASED', label: 'Revenue Based' },
];

export default function WIPPosting() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    fiscal_year: new Date().getFullYear(),
    period: new Date().getMonth() + 1,
    posting_date: new Date().toISOString().split('T')[0],
    wip_method: 'POC',
    test_run: true
  });
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      // Get released projects that are not closed
      const response = await psAPI.getProjects({ system_status: 'RELEASED' });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!formData.posting_date) {
      toast.error('Please enter a posting date');
      return;
    }

    try {
      setPosting(true);
      setResult(null);

      const response = await psAPI.postWIPToFI(formData);
      setResult(response.data);

      if (formData.test_run) {
        toast.success('WIP calculation completed (test run)');
      } else {
        toast.success('WIP posted to Finance successfully');
      }
    } catch (error) {
      console.error('Error posting WIP:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' 
        ? detail 
        : (Array.isArray(detail) 
            ? detail.map(e => e.msg || e).join(', ')
            : 'Failed to post WIP');
      toast.error(errorMsg);
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="wip-posting-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/ps/integration')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WIP Posting to FI</h1>
            <p className="text-gray-500 mt-1">Post Work-in-Progress costs and revenue to Finance</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-orange-600" />
                WIP Posting Parameters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Project *</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(v) => setFormData({ ...formData, project_id: v })}
                >
                  <SelectTrigger data-testid="project-select">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.project_id} - {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {projects.length === 0 && (
                  <p className="text-sm text-orange-600">
                    No released projects available. Release a project first.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fiscal Year *</Label>
                  <Input
                    type="number"
                    value={formData.fiscal_year}
                    onChange={(e) => setFormData({ ...formData, fiscal_year: parseInt(e.target.value) || '' })}
                    data-testid="fiscal-year-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Period *</Label>
                  <Input
                    type="number"
                    min="1"
                    max="16"
                    value={formData.period}
                    onChange={(e) => setFormData({ ...formData, period: parseInt(e.target.value) || '' })}
                    data-testid="period-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Posting Date *</Label>
                <Input
                  type="date"
                  value={formData.posting_date}
                  onChange={(e) => setFormData({ ...formData, posting_date: e.target.value })}
                  data-testid="posting-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label>WIP Method</Label>
                <Select
                  value={formData.wip_method}
                  onValueChange={(v) => setFormData({ ...formData, wip_method: v })}
                >
                  <SelectTrigger data-testid="wip-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WIP_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                <Switch
                  checked={formData.test_run}
                  onCheckedChange={(checked) => setFormData({ ...formData, test_run: checked })}
                  data-testid="test-run-switch"
                />
                <div>
                  <Label className="font-medium">Test Run</Label>
                  <p className="text-xs text-gray-500">
                    Preview WIP calculation without creating FI documents
                  </p>
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={handleSubmit}
                disabled={posting || !formData.project_id}
                data-testid="submit-btn"
              >
                {posting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    {formData.test_run ? <Eye className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                    {formData.test_run ? 'Run Test' : 'Post WIP'}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Result Display */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">WIP Result</CardTitle>
            </CardHeader>
            <CardContent>
              {!result ? (
                <div className="text-center py-12 text-gray-500">
                  <TrendingUp className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p>Select a project and run WIP calculation</p>
                  <p className="text-sm mt-1">Results will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Status */}
                  <div className={`flex items-center gap-2 p-3 rounded-lg ${
                    result.posted ? 'bg-green-50 text-green-800' : 'bg-orange-50 text-orange-800'
                  }`}>
                    {result.posted ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <AlertCircle className="h-5 w-5" />
                    )}
                    <div>
                      <p className="font-medium">{result.message}</p>
                      <p className="text-xs">{result.test_run ? 'Test Run - No documents created' : 'FI documents created'}</p>
                    </div>
                  </div>

                  {/* Project Info */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Project</p>
                      <p className="font-medium">{result.project_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Period</p>
                      <p className="font-medium">{result.fiscal_year}/{String(result.period).padStart(2, '0')}</p>
                    </div>
                  </div>

                  {/* WIP Amounts */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-orange-50 rounded-lg p-4">
                      <p className="text-sm text-orange-600 font-medium">WIP Costs</p>
                      <p className="text-xl font-bold text-orange-900 mt-1">
                        ${(result.wip_costs_posted || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-green-600 font-medium">WIP Revenue</p>
                      <p className="text-xl font-bold text-green-900 mt-1">
                        ${(result.wip_revenue_posted || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* FI Documents Created */}
                  {result.fi_documents && result.fi_documents.length > 0 && (
                    <div className="border rounded-lg p-3">
                      <p className="text-sm font-medium text-gray-600 mb-2">FI Documents Created</p>
                      <div className="flex flex-wrap gap-2">
                        {result.fi_documents.map((docNum) => (
                          <Badge key={docNum} variant="outline" className="font-mono">
                            {docNum}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.posted && result.posted_at && (
                    <p className="text-xs text-gray-500 text-right">
                      Posted at: {new Date(result.posted_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Section */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="border-l-4 border-orange-500 pl-3">
                <p className="font-medium">Percentage of Completion</p>
                <p className="text-gray-500">WIP = Costs / Budget * Expected Revenue</p>
              </div>
              <div className="border-l-4 border-green-500 pl-3">
                <p className="font-medium">Cost of Sales</p>
                <p className="text-gray-500">WIP = Total Costs - Recognized Revenue</p>
              </div>
              <div className="border-l-4 border-orange-500 pl-3">
                <p className="font-medium">Completed Contract</p>
                <p className="text-gray-500">WIP recognized only at project completion</p>
              </div>
              <div className="border-l-4 border-orange-500 pl-3">
                <p className="font-medium">Revenue Based</p>
                <p className="text-gray-500">WIP based on recognized revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
